import { useState, useCallback } from 'react'
import { useToken } from 'app/hooks'
import API from 'utils/api'
import * as antd from 'antd'

export interface PaymentOrder {
	id: number
	billId: string
	customerName: string
	customerPhone: string
	productNm: string
	amount: number
	status: 'W' | 'F' | 'C' | 'D'
	shortUrl?: string
	createdAt: string
	message?: string
	expireDt?: string
	billIssuer?: string
}

export interface SendBillPayload {
	customerName: string
	customerPhone: string
	productNm: string
	amount: number
	message?: string
	expireDt?: string
	billIssuer?: string
}

/** Single recipient for batch send */
export interface SendBillsRecipient {
	customerName: string
	customerPhone: string
}

export interface SendBillsPayload {
	productNm: string
	amount: number
	message?: string
	expireDt?: string
	billIssuer?: string
	recipients: SendBillsRecipient[]
}

export interface SendBillsResultItem {
	customerName: string
	customerPhone: string
	success: boolean
	billId?: string
	shortURL?: string
	orderId?: number
	message?: string
}

export interface SendBillsResult {
	successCount: number
	failCount: number
	results: SendBillsResultItem[]
}

export function usePaySSAMPayment(deptId?: number) {
	const { data: token } = useToken()
	const [orders, setOrders] = useState<PaymentOrder[]>([])
	const [loading, setLoading] = useState(false)
	const [actionLoading, setActionLoading] = useState<Record<string, boolean>>({})

	const fetchOrders = useCallback(async () => {
		if (!deptId || !token) return

		setLoading(true)
		try {
			const res = await API.get(`/payments/payssam/dept/${deptId}/orders?limit=50&offset=0`, token + '', {})
			if (res.result?.success && res.result?.result?.orders) {
				setOrders(res.result.result.orders.map((o: any) => ({
					id: o.id,
					billId: o.bill_id,
					customerName: o.customer_name,
					customerPhone: o.customer_phone,
					productNm: o.product_nm,
					amount: o.amount,
					status: o.status,
					shortUrl: o.short_url,
					createdAt: o.created_at,
					message: o.message,
					expireDt: o.expire_dt,
					billIssuer: o.bill_issuer,
				})))
			} else {
				throw new Error(res.result?.message || '주문 목록을 불러오는데 실패했습니다.')
			}
		} catch (err: any) {
			antd.message.error('주문 목록을 불러오는데 실패했습니다.')
			console.error(err)
			throw err
		} finally {
			setLoading(false)
		}
	}, [deptId, token])

	const sendBill = useCallback(async (payload: SendBillPayload) => {
		if (!deptId || !token) {
			throw new Error('지점 정보를 찾을 수 없습니다.')
		}

		try {
			const res = await API.post(`/payments/payssam/dept/${deptId}/bills/send`, token + '', payload)
			if (res.result?.success) {
				antd.message.success('청구서가 발송되었습니다.')
				await fetchOrders()
				return res.result.result
			} else {
				throw new Error(res.result?.message || '청구서 발송에 실패했습니다.')
			}
		} catch (err: any) {
			const errorMsg = err.message || '청구서 발송 중 오류가 발생했습니다.'
			antd.message.error(errorMsg)
			console.error(err)
			throw err
		}
	}, [deptId, token, fetchOrders])

	const sendBills = useCallback(async (payload: SendBillsPayload): Promise<SendBillsResult | null> => {
		if (!deptId || !token) {
			throw new Error('지점 정보를 찾을 수 없습니다.')
		}
		if (!payload.recipients?.length) {
			antd.message.warning('수신자를 선택해 주세요.')
			return null
		}
		try {
			const res = await API.post(`/payments/payssam/dept/${deptId}/bills/send/batch`, token + '', payload)
			const resultPayload = res?.result?.result
			if (res?.result?.success && resultPayload) {
				const { successCount, failCount } = resultPayload
				if (failCount === 0) {
					antd.message.success(`청구서 ${successCount}건이 발송되었습니다.`)
				} else if (successCount > 0) {
					antd.message.warning(`발송 완료 ${successCount}건, 실패 ${failCount}건입니다.`)
				} else {
					antd.message.error('모든 발송에 실패했습니다.')
				}
				await fetchOrders()
				return resultPayload as SendBillsResult
			}
			throw new Error(res?.result?.message || '청구서 일괄 발송에 실패했습니다.')
		} catch (err: any) {
			const errorMsg = err.message || '청구서 발송 중 오류가 발생했습니다.'
			antd.message.error(errorMsg)
			console.error(err)
			throw err
		}
	}, [deptId, token, fetchOrders])

	const readBill = useCallback(async (billId: string) => {
		if (!deptId || !token) return

		const key = `read-${billId}`
		setActionLoading(prev => ({ ...prev, [key]: true }))
		try {
			const res = await API.post(`/payments/payssam/dept/${deptId}/bills/${billId}/read`, token + '', {})
			if (res.result?.success) {
				antd.message.success('청구서 상태를 조회했습니다.')
				await fetchOrders()
				return res.result.result
			} else {
				throw new Error(res.result?.message || '조회에 실패했습니다.')
			}
		} catch (err: any) {
			const errorMsg = err.message || '조회 중 오류가 발생했습니다.'
			antd.message.error(errorMsg)
			console.error(err)
			throw err
		} finally {
			setActionLoading(prev => ({ ...prev, [key]: false }))
		}
	}, [deptId, token, fetchOrders])

	const resendBill = useCallback(async (billId: string) => {
		if (!deptId || !token) return

		const key = `resend-${billId}`
		setActionLoading(prev => ({ ...prev, [key]: true }))
		try {
			const res = await API.post(`/payments/payssam/dept/${deptId}/bills/${billId}/resend`, token + '', {})
			if (res.result?.success) {
				antd.message.success('청구서를 재전송했습니다.')
				await fetchOrders()
				return res.result.result
			} else {
				throw new Error(res.result?.message || '재전송에 실패했습니다.')
			}
		} catch (err: any) {
			const errorMsg = err.message || '재전송 중 오류가 발생했습니다.'
			antd.message.error(errorMsg)
			console.error(err)
			throw err
		} finally {
			setActionLoading(prev => ({ ...prev, [key]: false }))
		}
	}, [deptId, token, fetchOrders])

	const cancelBill = useCallback(async (billId: string, price: number) => {
		if (!deptId || !token) return

		const confirmed = window.confirm('정말로 결제를 취소하시겠습니까?')
		if (!confirmed) {
			return null
		}

		const key = `cancel-${billId}`
		setActionLoading(prev => ({ ...prev, [key]: true }))
		try {
			const res = await API.post(`/payments/payssam/dept/${deptId}/bills/${billId}/cancel`, token + '', { price })
			if (res.result?.success) {
				antd.message.success('결제가 취소되었습니다.')
				await fetchOrders()
				return res.result.result
			} else {
				throw new Error(res.result?.message || '취소에 실패했습니다.')
			}
		} catch (err: any) {
			const errorMsg = err.message || '취소 중 오류가 발생했습니다.'
			antd.message.error(errorMsg)
			console.error(err)
			throw err
		} finally {
			setActionLoading(prev => ({ ...prev, [key]: false }))
		}
	}, [deptId, token, fetchOrders])

	const destroyBill = useCallback(async (billId: string, price: number) => {
		if (!deptId || !token) return

		const confirmed = window.confirm('정말로 청구서를 파기하시겠습니까?')
		if (!confirmed) {
			return null
		}

		const key = `destroy-${billId}`
		setActionLoading(prev => ({ ...prev, [key]: true }))
		try {
			const res = await API.post(`/payments/payssam/dept/${deptId}/bills/${billId}/destroy`, token + '', { price })
			if (res.result?.success) {
				antd.message.success('청구서가 파기되었습니다.')
				await fetchOrders()
				return res.result.result
			} else {
				throw new Error(res.result?.message || '파기에 실패했습니다.')
			}
		} catch (err: any) {
			const errorMsg = err.message || '파기 중 오류가 발생했습니다.'
			antd.message.error(errorMsg)
			console.error(err)
			throw err
		} finally {
			setActionLoading(prev => ({ ...prev, [key]: false }))
		}
	}, [deptId, token, fetchOrders])

	return {
		orders,
		loading,
		actionLoading,
		fetchOrders,
		sendBill,
		sendBills,
		readBill,
		resendBill,
		cancelBill,
		destroyBill,
	}
}
